
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Partner, PartnerWithDetails, Domain, Association, PartnerPricing } from '@/types/partner';

export const usePartners = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getPartnerById = async (partnerId: string): Promise<PartnerWithDetails | null> => {
    try {
      setLoading(true);
      
      // Fetch partner details
      const { data: partner, error: partnerError } = await supabase
        .from('partners')
        .select('*')
        .eq('id', partnerId)
        .single();
      
      if (partnerError) {
        throw partnerError;
      }
      
      // Fetch associations
      const { data: associations, error: associationsError } = await supabase
        .from('partner_associations')
        .select(`
          associations (
            id,
            name,
            description,
            created_at
          )
        `)
        .eq('partner_id', partnerId);
      
      if (associationsError) {
        throw associationsError;
      }
      
      // Fetch pricing
      const { data: pricing, error: pricingError } = await supabase
        .from('partner_pricing')
        .select('*')
        .eq('partner_id', partnerId);
      
      if (pricingError) {
        throw pricingError;
      }
      
      // Fetch domains
      const { data: partnerDomains, error: domainsError } = await supabase
        .from('partner_domains')
        .select(`
          domains (
            id,
            name,
            description,
            image,
            status,
            created_at
          )
        `)
        .eq('partner_id', partnerId);
      
      if (domainsError) {
        throw domainsError;
      }
      
      // Process and cast the data to match the expected types
      const typedAssociations = associations?.map(a => {
        const assoc = a.associations;
        return {
          ...assoc,
          // Ensure name is one of the allowed values
          name: assoc.name as Association['name']
        };
      }) || [];
      
      const typedDomains = partnerDomains?.map(d => {
        const domain = d.domains;
        return {
          ...domain,
          // Ensure status is one of the allowed values
          status: domain.status as Domain['status']
        };
      }) || [];
      
      // Combine all data and ensure status is properly typed
      const partnerWithDetails: PartnerWithDetails = {
        ...partner,
        // Explicitly cast status to the expected type
        status: (partner.status || 'inactive') as 'active' | 'inactive',
        associations: typedAssociations,
        pricing: pricing || [],
        domains: typedDomains,
      };
      
      return partnerWithDetails;
    } catch (error) {
      console.error('Error fetching partner by ID:', error);
      setError(error.message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const getPartnersByType = async (associationName: string): Promise<PartnerWithDetails[]> => {
    try {
      setLoading(true);
      setError(null);
  
      // Fetch partners associated with the specified association name
      const { data: partners, error: partnersError } = await supabase
        .from('partners')
        .select(`
          *,
          associations:partner_associations (
            associations (
              id,
              name,
              description,
              created_at
            )
          ),
          domains:partner_domains (
            domains (
              id,
              name,
              description,
              image,
              status,
              created_at
            )
          ),
          pricing:partner_pricing(*)
        `)
        .contains('associations', [{ associations: { name: associationName } }]);
  
      if (partnersError) {
        console.error("Error fetching partners:", partnersError);
        setError(partnersError.message);
        return [];
      }
  
      // Type cast the data to PartnerWithDetails[] with proper status type
      const typedPartners: PartnerWithDetails[] = partners.map((partner: any) => ({
        ...partner,
        // Explicitly cast status to the expected type
        status: (partner.status || 'inactive') as 'active' | 'inactive',
        associations: partner.associations?.map((a: any) => ({
          ...a.associations,
          name: a.associations.name as Association['name']
        })) || [],
        domains: partner.domains?.map((d: any) => ({
          ...d.domains,
          status: d.domains.status as Domain['status']
        })) || [],
        pricing: partner.pricing || [],
      }));
  
      return typedPartners;
    } catch (error) {
      console.error("Error fetching partners:", error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  };

  return {
    getPartnersByType,
    getPartnerById,
    loading,
    error
  };
};

export default usePartners;
